import {
  Outlet,
  NavLink,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";
import { useAuth } from "@/context/AuthContext";
import { Badge, Button } from "@/components/kit";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import Logo from "@/components/Logo";
import OnboardingStepper from "@/components/OnboardingStepper";
import {
  LayoutDashboard,
  Radar,
  Users,
  Smartphone,
  FileText,
  Send,
  BarChart3,
  CreditCard,
  LogOut,
  Zap,
  Menu,
  X,
  ShieldAlert,
  MapPinned,
  Facebook,
  Instagram,
  Mail,
  LifeBuoy,
  History,
  Sparkles,
  Megaphone,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard" },
  {
    icon: Radar,
    label: "Scraper",
    base: "/app/scraper",
    children: [
      { to: "/app/scraper/google-maps", icon: MapPinned, label: "Google Maps" },
      { to: "/app/scraper/facebook", icon: Facebook, label: "Facebook" },
      { to: "/app/scraper/instagram", icon: Instagram, label: "Instagram" },
    ],
  },
  { to: "/app/leads", icon: Users, label: "Leads" },
  { to: "/app/devices", icon: Smartphone, label: "WA Devices" },
  { to: "/app/templates", icon: FileText, label: "Template" },
  { to: "/app/blast", icon: Send, label: "WA Blast" },
  { to: "/app/email-blast", icon: Mail, label: "Email Blast" },
  { to: "/app/blast-history", icon: History, label: "Riwayat Blast" },
  { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/app/usage", icon: Zap, label: "Pemakaian" },
  { to: "/app/billing", icon: CreditCard, label: "Billing" },
  { to: "/app/community", icon: Sparkles, label: "Community Tips" },
  { to: "/app/bantuan", icon: LifeBuoy, label: "Bantuan" },
];

function NavGroup({ item, onNavigate }) {
  const location = useLocation();
  const groupActive = location.pathname.startsWith(item.base);
  return (
    <div
      data-testid="nav-scraper-group"
      className={`my-1.5 rounded-2xl p-2 bg-gradient-to-br from-sky-50 via-white to-white ring-1 transition ${
        groupActive
          ? "ring-sky-200 shadow-sm shadow-sky-100/70"
          : "ring-slate-100"
      }`}
    >
      <div className="flex items-center gap-2 px-2 pb-2 pt-0.5">
        <span className="grid place-items-center w-6 h-6 rounded-lg bg-sky-500 text-white shadow-sm shadow-sky-200">
          <item.icon className="w-[15px] h-[15px]" />
        </span>
        <span className="text-[13px] font-bold text-slate-800 tracking-tight">
          {item.label}
        </span>
        <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-sky-600 bg-sky-100 px-1.5 py-0.5 rounded-full">
          Cari Lead
        </span>
      </div>
      <div className="space-y-1">
        {item.children.map((c) => (
          <NavLink
            key={c.to}
            to={c.to}
            onClick={onNavigate}
            data-testid={`nav-${c.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive
                  ? "bg-sky-500 text-white shadow-md shadow-sky-200 translate-x-0.5"
                  : "text-slate-600 hover:bg-white hover:shadow-sm hover:translate-x-0.5"
              }`
            }
          >
            <c.icon className="w-4 h-4" />
            {c.label}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function QuotaBar({ label, used, limit }) {
  const pct =
    limit === -1 ? 0 : limit === 0 ? 0 : Math.min((used / limit) * 100, 100);
  return (
    <div data-testid={`quota-${label.toLowerCase()}`}>
      <div className="flex justify-between text-[11px] text-slate-500 mb-1">
        <span>{label}</span>
        <span className="font-semibold text-slate-700">
          {used}/{limit === -1 ? "∞" : limit}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-sky-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, quota, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-white border-r border-slate-100 flex flex-col h-full">
      <Link
        to="/app"
        className="flex items-center gap-2 px-6 h-16 border-b border-slate-100"
      >
        <Logo to={null} iconSize={34} showVersion />
      </Link>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((n) =>
          n.children ? (
            <NavGroup
              key={n.label}
              item={n}
              onNavigate={() => setOpen(false)}
            />
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive
                    ? "bg-sky-50 text-sky-700"
                    : "text-slate-600 hover:bg-slate-50"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ),
        )}
        {user?.role === "admin" && (
          <NavLink
            to="/app/admin"
            onClick={() => setOpen(false)}
            data-testid="nav-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                isActive
                  ? "bg-slate-900 text-sky-400"
                  : "text-slate-600 hover:bg-slate-50"
              }`
            }
          >
            <ShieldAlert className="w-[18px] h-[18px]" />
            Admin
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink
            to="/app/admin/ads"
            onClick={() => setOpen(false)}
            data-testid="nav-admin-ads"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                isActive
                  ? "bg-rose-50 text-rose-700"
                  : "text-slate-600 hover:bg-slate-50"
              }`
            }
          >
            <Megaphone className="w-[18px] h-[18px]" />
            Ads Manager
          </NavLink>
        )}
      </nav>

      {quota && quota.credits && (
        <div className="px-4 py-4 border-t border-slate-100 space-y-3">
          <QuotaBar
            label="Kredit"
            used={quota.credits.credits_used}
            limit={quota.credits.monthly_credits}
          />
          {quota.credits.payg_balance > 0 && (
            <p className="text-[11px] text-emerald-600 font-semibold">
              +{quota.credits.payg_balance} kredit PAYG
            </p>
          )}
        </div>
      )}

      <div className="px-4 py-4 border-t border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-sky-100 grid place-items-center text-sky-700 font-bold text-sm">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">
              {user?.name}
            </p>
            <Badge color="sky" className="capitalize mt-0.5">
              {user?.plan}
            </Badge>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-rose-500 transition w-full px-1"
        >
          <LogOut className="w-4 h-4" /> Keluar
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen overflow-hidden bg-slate-50 flex">
      <div className="hidden lg:block h-screen">{Sidebar}</div>

      {/* Mobile */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/30"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 h-screen">
        <header className="h-16 shrink-0 bg-white/80 glass border-b border-slate-100 flex items-center justify-between px-4 lg:px-8 z-30">
          <button className="lg:hidden" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            {quota && quota.credits && (
              <Badge
                color="slate"
                className="hidden sm:inline-flex"
                data-testid="navbar-credit"
              >
                Sisa kredit:{" "}
                {(quota.credits.available || 0).toLocaleString("id-ID")}
              </Badge>
            )}
            <Link to="/app/billing">
              <Button
                size="sm"
                data-testid="upgrade-plan-btn"
                className="shadow-md shadow-sky-200"
              >
                <Zap className="w-4 h-4" fill="white" /> Upgrade Plan
              </Button>
            </Link>
            <UserButton
              afterSignOutUrl="/"
              data-testid="user-button"
              appearance={{
                elements: {
                  avatarBox:
                    "w-9 h-9 ring-2 ring-sky-100 hover:ring-sky-300 transition",
                  userButtonPopoverCard: "shadow-xl rounded-2xl",
                },
              }}
            />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-[1600px] w-full mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      <FloatingWhatsApp />
      {user && user !== false && !user.onboarded && <OnboardingStepper />}
    </div>
  );
}
